import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  Button,
  CircularProgress,
  Divider,
  Snackbar,
  Alert,
} from "@mui/material";
import AdminNav from "./AdminNav";

import api from "../api/api";
import { ENDPOINTS } from "../api/endpoints";
import { parseLocationCodes } from "../utils/parseLocationCodes";

const pageSize = 50;

export default function PostedNoticesPage() {
  const { email } = useParams();

  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [notices, setNotices] = useState([]);
  const [total, setTotal] = useState(0);
  const [locationsLookup, setLocationsLookup] = useState({});
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  /* ----------------------------------------------------------
     Fetch Locations once
  ---------------------------------------------------------- */
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const res = await api.get(ENDPOINTS.getLocations());
        const raw = res.data?.data || [];

        const lookup = {};

        raw.forEach((loc) => {
          if (!loc.locationCode) return;

          lookup[loc.locationCode] = loc.locationCity || "";

          const base = loc.locationCode.replace(/[0-9]/g, "");
          if (!lookup[base]) lookup[base] = loc.locationCity;
        });

        setLocationsLookup(lookup);
      } catch (err) {
        console.error("Location fetch failed", err);
      }
    };

    fetchLocations();
  }, []);

  /* ----------------------------------------------------------
     Fetch notices for this email
  ---------------------------------------------------------- */
  useEffect(() => {
    loadPage(page);
  }, [email, page]);

  const loadPage = async (pageNum) => {
    setLoading(true);

    try {
      const res = await api.get(
        `/api/NewCastingNotice/GetNewCastingNoticeDetails?noticeId=0&page=${pageNum}&pageSize=${pageSize}&email=${encodeURIComponent(
          email
        )}`
      );

      setNotices(res.data?.data || []);
      setTotal(res.data?.count || 0);
    } catch (err) {
      console.error("Load notices failed", err);
      setToast({
        open: true,
        message: "Failed to fetch notices",
        severity: "error",
      });
    }

    setLoading(false);
  };

  /* ----------------------------------------------------------
     Parse human-readable locations
  ---------------------------------------------------------- */
  const getLocationNames = (locString) => {
    const codes = parseLocationCodes(locString || "");

    const readable = codes
      .map((c) => {
        if (locationsLookup[c]) return locationsLookup[c];

        const base = c.replace(/[0-9]/g, "");
        if (locationsLookup[base]) return locationsLookup[base];

        const alt = Object.keys(locationsLookup).find((k) =>
          k.startsWith(base)
        );
        if (alt) return locationsLookup[alt];

        return null;
      })
      .filter((n, i, arr) => n && arr.indexOf(n) === i);

    return readable.join(", ");
  };

  /* ----------------------------------------------------------
     Render notice card
  ---------------------------------------------------------- */
  const renderCard = (notice) => {
    return (
      <Card sx={{ mb: 2, p: 2 }} key={notice.noticeId}>
        <Typography fontWeight={700} fontSize="16px">
          {notice.projectTitle}
        </Typography>

        <Typography fontSize="14px" mt={1}>
          <b>Location:</b> {getLocationNames(notice.auditionLocations)}
        </Typography>

        <Typography fontSize="14px" mt={1}>
          <b>Deadline:</b> {new Date(notice.noticeEndDate).toLocaleDateString()}
        </Typography>

        <Typography
          mt={1}
          sx={{
            display: "inline-block",
            px: 1.5,
            py: 0.5,
            borderRadius: "6px",
            fontWeight: 600,
            fontSize: "13px",
            backgroundColor:
              notice.noticeStatus === "Approved"
                ? "#C8F7C5"
                : notice.noticeStatus === "Pending"
                ? "#d17c7c"
                : "#d32f2f",
            color: notice.noticeStatus === "Approved" ? "#000" : "#fff",
          }}
        >
          {notice.noticeStatus}
        </Typography>

        <Box display="flex" justifyContent="flex-end" mt={2} gap={1}>
          <Button
            variant="outlined"
            size="small"
            onClick={() =>
              window.open(`/view-notice/${notice.noticeId}`, "_blank")
            }
          >
            View
          </Button>

          <Button
            variant="contained"
            size="small"
            onClick={() => {
              window.location.href = `/post-casting-call/${notice.noticeId}`;
            }}
          >
            Edit
          </Button>
        </Box>
      </Card>
    );
  };

  return (
    <>
      <AdminNav />

      <Box sx={{ maxWidth: "1200px", mx: "auto", p: 3 }}>
        <Typography variant="h4" fontWeight={700}>
          Posted Notices
        </Typography>

        <Typography variant="subtitle1" color="text.secondary" mt={1} mb={3}>
          Showing notices for <b>{email}</b>
        </Typography>

        {loading ? (
          <Box textAlign="center" mt={5}>
            <CircularProgress />
          </Box>
        ) : notices.length === 0 ? (
          <Typography>No notices found.</Typography>
        ) : (
          <>
            {notices.map((notice) => renderCard(notice))}

            {/* Pagination */}
            <Box display="flex" justifyContent="space-between" mt={3} mb={5}>
              <Button
                variant="outlined"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </Button>

              <Button
                variant="outlined"
                disabled={page * pageSize >= total}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </Box>
          </>
        )}
      </Box>

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={toast.severity}>{toast.message}</Alert>
      </Snackbar>
    </>
  );
}
